/* eslint-disable @typescript-eslint/naming-convention */
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { CasinoGameHeader, CasinoGameList, useWidgetAnchor } from '@swf/casino';
import { CacheControl, Vary } from '@swf/components/ResponseHeaders/ResponseHeaders';
import type { SliderHandles, State as SliderState } from '@swf/components/Slider/Slider';
import { Header } from '@swf/core/enums';
import { select } from '@swf/core/store';
import type { RootState } from '@swf/core/store';
import type { WidgetFC } from '@swf/core/types/Widget';
import { getStyle, isVisibleByAuth } from '@swf/core/utils/casino';

import styles from './CasinoPlayerGamesWidget.scss';
import { playerGamesWidgetModel, usePlayerGamesWidgetModel } from './model';

const CasinoPlayerGamesWidget: WidgetFC = () => {
  const { widgetSelect, widgetConfig } = usePlayerGamesWidgetModel();
  const { games } = useSelector(widgetSelect.get);
  const title = useSelector((state: RootState) => widgetSelect.getTitle(state, widgetConfig));
  const isAuthenticated = useSelector(select.session.isClientAuthenticated);
  const [sliderState, setSliderState] = useState<SliderState>({ left: false, right: false });
  const sliderRef = React.createRef<SliderHandles>();
  const container = React.createRef<HTMLDivElement>();
  const {
    layout,
    sub_title: subTitle,
    min_games: minGames,
    rows,
    logged_in_only: loggedInOnly,
    hide_on_login: hideOnLogin
  } = widgetConfig;
  const shouldRender =
    games?.length >= minGames && isVisibleByAuth(loggedInOnly, hideOnLogin, isAuthenticated);
  const icon = useSelector((state: RootState) => widgetSelect.getIcon(state, widgetConfig));
  const widgetTargetId = useWidgetAnchor();

  return shouldRender ? (
    <section
      className={styles.container}
      ref={container}
      id={widgetTargetId}
      style={getStyle(layout)}
    >
      <CacheControl>public, max-age=600</CacheControl>
      <Vary by={[Header.Currency]} />
      <CasinoGameHeader
        title={title}
        subTitle={subTitle}
        icon={icon}
        sliderRef={sliderRef}
        sliderState={sliderState}
      />
      <CasinoGameList
        {...widgetConfig}
        games={games}
        rows={rows}
        icon={icon}
        sliderRef={sliderRef}
        onScroll={setSliderState}
        mosaic={widgetConfig.mosaic_layout}
        params={{ include_tags: widgetConfig.params.include_tags }}
      />
    </section>
  ) : null;
};

CasinoPlayerGamesWidget.model = playerGamesWidgetModel;
export default CasinoPlayerGamesWidget;
